import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/ui/use-toast";
import {
  DEFAULT_TIMEZONE,
  GAP_MAX,
  GAP_MIN,
  PER_DAY_MAX,
  PER_DAY_MIN,
  PLATFORM_DEFAULT_GAP_MINUTES,
  PLATFORM_DEFAULT_PER_DAY,
  fromColumn,
  gapError,
  perDayError,
  timezoneOptions,
  toColumn,
} from "@/lib/callLimits";
import { supabase } from "../../../supabase/supabase";

interface SavedCallLimits {
  max_calls_per_number_per_day: number | null;
  min_call_gap_minutes: number | null;
  timezone: string | null;
}

interface CallLimitsSettingProps {
  organizationId: string;
  saved: SavedCallLimits;
  onSaved: (saved: SavedCallLimits) => void;
}

// How often one phone number may be called by the organization's assistants (V2 calls).
export default function CallLimitsSetting({ organizationId, saved, onSaved }: CallLimitsSettingProps) {
  const savedPerDay = fromColumn(saved.max_calls_per_number_per_day);
  const savedGap = fromColumn(saved.min_call_gap_minutes);
  const savedZone = saved.timezone || "";
  const [perDay, setPerDay] = useState(savedPerDay);
  const [gap, setGap] = useState(savedGap);
  const [zone, setZone] = useState(savedZone);
  const [isSaving, setIsSaving] = useState(false);

  const perDayProblem = perDayError(perDay);
  const gapProblem = gapError(gap);
  const changed = perDay !== savedPerDay || gap !== savedGap || zone !== savedZone;

  const save = async () => {
    setIsSaving(true);
    try {
      // Row level security lets only owners, admins and platform admins update the row. A
      // blocked update is not an error: it changes no row, so ask for the rows back.
      const { data, error } = await supabase
        .from("organizations")
        .update({
          max_calls_per_number_per_day: toColumn(perDay),
          min_call_gap_minutes: toColumn(gap),
          timezone: zone || null,
        })
        .eq("id", organizationId)
        .select("id, max_calls_per_number_per_day, min_call_gap_minutes, timezone");

      if (error) throw error;

      if (!data || data.length === 0) {
        setPerDay(savedPerDay);
        setGap(savedGap);
        setZone(savedZone);
        toast({
          title: "Not saved",
          description: "Only an organization owner or admin can change call limits.",
          variant: "destructive",
        });
        return;
      }

      const row = data[0];
      onSaved({
        max_calls_per_number_per_day: row.max_calls_per_number_per_day ?? null,
        min_call_gap_minutes: row.min_call_gap_minutes ?? null,
        timezone: row.timezone ?? null,
      });
      toast({ title: "Call limits saved", description: "They apply to the next call." });
    } catch (error: any) {
      console.error("Error saving call limits:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to save the call limits",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-call-limits="organization">
      <div className="space-y-1">
        <h3 className="text-lg font-medium">Call Limits</h3>
        <p className="text-sm text-muted-foreground">
          How often one phone number may be called by this organization's assistants, all of them
          together (V2 calls). Only calls the person picks up count toward these. Every dial also
          counts toward a fixed cap of 6 attempts a day, 30 minutes apart. Empty fields use the
          platform default of {PLATFORM_DEFAULT_PER_DAY} calls a day and{" "}
          {PLATFORM_DEFAULT_GAP_MINUTES} minutes between calls; the limit cannot be turned off.
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-3 max-w-3xl">
        <div className="space-y-1">
          <Label htmlFor="org-calls-per-day">Calls per number per day</Label>
          <Input
            id="org-calls-per-day"
            inputMode="numeric"
            placeholder={`${PLATFORM_DEFAULT_PER_DAY} (default)`}
            value={perDay}
            onChange={(e) => setPerDay(e.target.value)}
            aria-invalid={!!perDayProblem}
          />
          <p className="text-xs text-muted-foreground">{PER_DAY_MIN} to {PER_DAY_MAX}, a calendar day</p>
        </div>
        <div className="space-y-1">
          <Label htmlFor="org-call-gap">Minutes between calls</Label>
          <Input
            id="org-call-gap"
            inputMode="numeric"
            placeholder={`${PLATFORM_DEFAULT_GAP_MINUTES} (default)`}
            value={gap}
            onChange={(e) => setGap(e.target.value)}
            aria-invalid={!!gapProblem}
          />
          <p className="text-xs text-muted-foreground">{GAP_MIN} to {GAP_MAX}; 0 is no gap</p>
        </div>
        <div className="space-y-1">
          <Label htmlFor="org-call-timezone">Time zone of the day</Label>
          <Select value={zone || DEFAULT_TIMEZONE} onValueChange={(v) => setZone(v === DEFAULT_TIMEZONE ? "" : v)}>
            <SelectTrigger id="org-call-timezone">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {timezoneOptions(savedZone).map((z) => (
                <SelectItem key={z} value={z}>
                  {z === DEFAULT_TIMEZONE ? `${z} (default)` : z}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">When a new day starts</p>
        </div>
      </div>
      {(perDayProblem || gapProblem) && (
        <p className="text-sm text-destructive">{perDayProblem || gapProblem}</p>
      )}
      <Button onClick={save} disabled={isSaving || !changed || !!perDayProblem || !!gapProblem}>
        {isSaving ? "Saving..." : "Save call limits"}
      </Button>
    </div>
  );
}
